import * as React from "react";
import { Check, RotateCcw } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { Input } from "../ui/Input";
import { Slider } from "../ui/Slider";
import { useToast } from "../ui/Toast";
import { useSpaceStore } from "../../stores/spaceStore";
import { getGeometryFrames, setPlaneFrame, type GeometryFrames, type SurfaceFrame } from "../../services/api";
import { applyEdit, angleBetween, FrameEdit, gridLines, NO_EDIT, onPlane, pivotOf } from "../../lib/planeFrame";
import { cn } from "../../lib/utils";

const LABEL: Record<string, string> = { floor: "Floor", ceiling: "Ceiling", walls: "Wall", stairs: "Stairs" };
const surfaceLabel = (s: SurfaceFrame) =>
  s.kind === "floor" || s.kind === "ceiling" ? LABEL[s.kind]
    : s.kind === "stairs" ? `Stairs ${s.index + 1} (${s.is_wall ? "riser" : "tread"})`
    : `${LABEL[s.kind]} ${s.index + 1}`;

const isEdited = (e?: FrameEdit) => !!e && (e.tilt !== 0 || e.turn !== 0 || e.pattern !== 0 || e.size !== 1);

/**
 * Geometry editor: each surface's texture frame drawn as a tile grid over the
 * photo, with the camera the renderer uses, so what you see is where tiles
 * land. Tilt/turn pivot on the surface's centre (the tile size in the middle
 * of the surface does not jump), pattern rotation spins the texture in the
 * plane, and size scales the tiles. Apply sends the exact frame shown.
 */
export function GeometryEditor() {
  const { spaceId, originalImage } = useSpaceStore();
  const { addToast } = useToast();

  const [frames, setFrames] = React.useState<GeometryFrames | null>(null);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [edits, setEdits] = React.useState<Record<string, FrameEdit>>({});
  const [tile, setTile] = React.useState("0.5");
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const imgRef = React.useRef<HTMLImageElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [displaySize, setDisplaySize] = React.useState<{ w: number; h: number } | null>(null);

  const load = React.useCallback(async () => {
    if (!spaceId) return;
    setLoading(true);
    try {
      const res = await getGeometryFrames(spaceId);
      if (res.status !== "SUCCESS") throw new Error(res.message || "Could not load geometry");
      setFrames(res);
      setSelected((cur) => (cur && res.surfaces.some((s) => s.name === cur) ? cur : res.surfaces[0]?.name ?? null));
    } catch (e) {
      addToast("error", e instanceof Error ? e.message : "Could not load geometry");
    } finally {
      setLoading(false);
    }
  }, [spaceId, addToast]);

  React.useEffect(() => {
    setEdits({});
    setFrames(null);
    load();
  }, [spaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the overlay canvas the size of the displayed photo
  React.useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const measure = () => setDisplaySize({ w: img.clientWidth, h: img.clientHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(img);
    measure();
    return () => ro.disconnect();
  }, [originalImage, frames]);

  const surface = frames?.surfaces.find((s) => s.name === selected) || null;
  const edit = (surface && edits[surface.name]) || NO_EDIT;
  const tileSize = Math.max(0.05, parseFloat(tile) || 0.5);

  const editedFrame = React.useMemo(() => {
    if (!surface || !frames) return null;
    const base = { plane: surface.plane, vertical: surface.vertical, horizontal: surface.horizontal, origin: surface.origin };
    return applyEdit(base, edit, pivotOf(base, surface.centroid, frames.intrinsics));
  }, [surface, frames, edit]);

  // ------------------------------------------------------------- drawing
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !frames || !displaySize) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(displaySize.w * dpr);
    canvas.height = Math.round(displaySize.h * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const s = displaySize.w / frames.width;
    ctx.setTransform(dpr * s, 0, 0, dpr * s, 0, 0);
    ctx.clearRect(0, 0, frames.width, frames.height);

    const tracePath = (polys: [number, number][][]) => {
      ctx.beginPath();
      polys.forEach((poly) => poly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))));
      ctx.closePath();
    };

    // every surface's outline, faint
    frames.surfaces.forEach((sf) => {
      sf.outline.forEach((poly) => {
        tracePath([poly]);
        ctx.lineWidth = 1.5 / s;
        ctx.strokeStyle = sf.name === selected ? "rgba(59,130,246,0.95)" : "rgba(255,255,255,0.45)";
        ctx.stroke();
      });
    });
    if (!surface || !editedFrame) return;

    ctx.save();
    tracePath(surface.outline);
    ctx.clip("evenodd");
    const outlinePts = surface.outline.flat();
    const drawGrid = (frame: typeof editedFrame, dashed: boolean) => {
      const lines = gridLines(frame, frames.intrinsics, outlinePts, tileSize, surface.texture_offset);
      lines.forEach(({ a, b, major }) => {
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.setLineDash(dashed ? [6 / s, 6 / s] : []);
        ctx.lineWidth = (major ? 2 : 1) / s;
        ctx.strokeStyle = dashed ? "rgba(255,255,255,0.35)" : major ? "rgba(250,204,21,0.95)" : "rgba(250,204,21,0.7)";
        ctx.stroke();
      });
    };
    if (isEdited(edit)) {
      drawGrid(onPlane({ plane: surface.plane, vertical: surface.vertical, horizontal: surface.horizontal, origin: surface.origin }), true);
    }
    drawGrid(editedFrame, false);
    ctx.restore();
  }, [frames, displaySize, selected, surface, editedFrame, tileSize, edit]);

  // ------------------------------------------------------------- actions
  const setEdit = (patch: Partial<FrameEdit>) => {
    if (!surface) return;
    setEdits((prev) => ({ ...prev, [surface.name]: { ...(prev[surface.name] || NO_EDIT), ...patch } }));
  };

  const apply = async () => {
    if (!surface || !editedFrame || !spaceId) return;
    setSaving(true);
    try {
      const res = await setPlaneFrame(surface.kind, surface.index, editedFrame, spaceId);
      if (res.status !== "SUCCESS") throw new Error(res.message || "Could not apply");
      addToast("success", `${surfaceLabel(surface)} updated — Deploy to publish`);
      setEdits((prev) => {
        const next = { ...prev };
        delete next[surface.name];
        return next;
      });
      await load();
    } catch (e) {
      addToast("error", e instanceof Error ? e.message : "Could not apply");
    } finally {
      setSaving(false);
    }
  };

  if (!spaceId) return null;

  const slider = (label: string, key: keyof FrameEdit, min: number, max: number, step: number, fmt: (v: number) => string) => (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">{fmt(edit[key])}</span>
      </div>
      <Slider min={min} max={max} step={step} value={[edit[key]]} onValueChange={([v]) => setEdit({ [key]: v })} />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Geometry</CardTitle>
        <CardDescription>
          The yellow grid is where tiles land, drawn with the same camera as the renderer. Adjust until it follows
          the surface in the photo, then Apply. Tilt and turn pivot on the surface's centre, so the tile size
          there stays the same.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 xl:flex-row">
        {/* min-w-0 lets the photo shrink below its natural width; the wrapper
            hugs the image so the overlay canvas sits exactly on it */}
        <div className="flex min-w-0 flex-1 justify-center">
          <div className="relative inline-block max-w-full select-none">
            {originalImage ? (
              <img
                ref={imgRef}
                src={originalImage}
                alt="Space"
                className="block h-auto w-auto max-w-full rounded-md"
                style={{ maxHeight: "65vh" }}
              />
            ) : (
              <div className="aspect-video max-w-full rounded-md bg-muted" style={{ width: 640 }} />
            )}
            <canvas ref={canvasRef} className="pointer-events-none absolute left-0 top-0" style={displaySize ? { width: displaySize.w, height: displaySize.h } : undefined} />
            {loading && <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/20 text-sm text-white">Loading geometry…</div>}
          </div>
        </div>

        <div className="w-full shrink-0 space-y-5 xl:w-80">
          <div className="space-y-2">
            {(["floor", "ceiling", "walls", "stairs"] as const).map((kind) => {
              const group = frames?.surfaces.filter((sf) => sf.kind === kind) || [];
              if (!group.length) return null;
              const single = kind === "floor" || kind === "ceiling";
              return (
                <div key={kind} className="flex flex-wrap items-center gap-1">
                  {!single && <span className="w-12 text-xs font-medium text-muted-foreground">{LABEL[kind]}</span>}
                  {group.map((sf) => (
                    <button
                      key={sf.name}
                      onClick={() => setSelected(sf.name)}
                      title={surfaceLabel(sf)}
                      className={cn(
                        "rounded-md border py-0.5 text-xs font-medium tabular-nums transition-colors",
                        single ? "px-2.5" : "px-1.5",
                        sf.name === selected ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
                      )}
                    >
                      {single ? LABEL[kind] : sf.index + 1}
                      {kind === "stairs" && <span className="ml-0.5 opacity-60">{sf.is_wall ? "r" : "t"}</span>}
                      {isEdited(edits[sf.name]) && <span className="ml-0.5">•</span>}
                    </button>
                  ))}
                </div>
              );
            })}
            {surface && <p className="text-sm font-medium">{surfaceLabel(surface)}</p>}
          </div>

          {frames?.intrinsics.estimated && (
            <p className="rounded-md bg-amber-500/10 px-2 py-1.5 text-xs">
              This space was analysed without a measured camera, so the grid uses an estimated one and may be
              slightly off everywhere. Re-analysing the photo fixes that.
            </p>
          )}

          {surface && (
            <>
              {slider("Tilt", "tilt", -20, 20, 0.1, (v) => `${v.toFixed(1)}°`)}
              {slider("Turn", "turn", -20, 20, 0.1, (v) => `${v.toFixed(1)}°`)}
              <div className="space-y-1.5">
                {slider("Pattern rotation", "pattern", -180, 180, 1, (v) => `${v.toFixed(0)}°`)}
                <div className="flex gap-1">
                  {[0, 45, 90, -45].map((a) => (
                    <Button key={a} size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEdit({ pattern: a })}>
                      {a}°
                    </Button>
                  ))}
                </div>
              </div>
              {slider("Size", "size", 0.5, 2, 0.01, (v) => `${Math.round(100 / v)}% tiles`)}

              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Grid</span>
                <Input value={tile} onChange={(e) => setTile(e.target.value)} className="h-8 w-20" inputMode="decimal" />
                <span className="text-muted-foreground">m</span>
              </div>

              {isEdited(edit) && editedFrame && (
                <p className="text-xs text-muted-foreground">
                  Plane changed by {angleBetween(editedFrame.plane, surface.plane).toFixed(1)}°. The dashed grid is the current one.
                </p>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEdit(NO_EDIT)} disabled={!isEdited(edit) || saving}>
                  <RotateCcw className="mr-1 h-4 w-4" /> Reset
                </Button>
                <Button onClick={apply} disabled={!isEdited(edit)} loading={saving}>
                  <Check className="mr-1 h-4 w-4" /> Apply
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
